import React from 'react';

import { useTranslation } from 'react-i18next';

import CheckCircle from '@/assets/svgs/check-circle.svg';
import { cn } from '@/lib/utils';
import { EntityType } from '@/modules/common/types/entity-type';
import { DataReconciliationRoutes } from '@/router/routes';
import { invertMap } from '@/utils/map-extenstions';

export enum DataReconciliationStep {
  Team = 'team',
  Clients = 'clients',
  Projects = 'projects',
}

export const entityTypeToStepMap = new Map<EntityType, DataReconciliationStep>([
  [EntityType.User, DataReconciliationStep.Team],
  [EntityType.Client, DataReconciliationStep.Clients],
  [EntityType.Project, DataReconciliationStep.Projects],
]);

export const stepToEntityTypeMap = invertMap(entityTypeToStepMap);

export const dataReconciliationStepToRoute: Record<
  DataReconciliationStep,
  DataReconciliationRoutes
> = {
  [DataReconciliationStep.Team]: DataReconciliationRoutes.Team,
  [DataReconciliationStep.Clients]: DataReconciliationRoutes.Clients,
  [DataReconciliationStep.Projects]: DataReconciliationRoutes.Projects,
};

interface DataReconciliationSidebarProps {
  currentStep: DataReconciliationStep;
  unresolvedSteps: Set<DataReconciliationStep>;
  resolvedSteps: Set<DataReconciliationStep>;
}

export const DataReconciliationSidebar: React.FC<DataReconciliationSidebarProps> = ({
  currentStep,
  unresolvedSteps,
  resolvedSteps,
}) => {
  const { t } = useTranslation('authenticated.dataReconciliation');

  const allSteps = [
    {
      id: DataReconciliationStep.Team,
      label: t('steps.team'),
      entityType: EntityType.User,
    },
    {
      id: DataReconciliationStep.Clients,
      label: t('steps.clients'),
      entityType: EntityType.Client,
    },
    {
      id: DataReconciliationStep.Projects,
      label: t('steps.projects'),
      entityType: EntityType.Project,
    },
  ];

  const steps = allSteps.filter(step => unresolvedSteps.has(step.id) || resolvedSteps.has(step.id));

  return (
    <div className="flex w-full max-w-xs flex-col gap-6 border-r border-border bg-muted/30 p-6">
      <div className="flex flex-col gap-4">
        {steps.map((step, idx) => {
          const isActive = currentStep === step.id;
          const isResolved = resolvedSteps.has(step.id);

          return (
            <div key={step.id} className="flex flex-row items-center gap-2">
              {isResolved ? (
                <img
                  alt={t('sidebar.passedStep')}
                  src={CheckCircle}
                  className="size-6 text-success"
                />
              ) : (
                <div
                  className={cn(
                    'flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'border-2 border-muted-foreground text-muted-foreground'
                  )}
                >
                  {idx + 1}
                </div>
              )}

              <div className="flex flex-1 flex-col">
                <span
                  className={cn(
                    'text-sm font-medium',
                    isActive ? 'text-foreground' : 'text-muted-foreground'
                  )}
                >
                  {step.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
